import { useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { DataTable } from "@/components/ui-system";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertCircle, Download, FileSpreadsheet, Loader2, Upload, X } from "lucide-react";

// Keep in lockstep with models/b2c_order.MAX_BULK_QUOTE_ROWS (backend enforces it).
const MAX_ROWS = 100;

const downloadBlob = async (url, filename) => {
  const { data } = await axios.get(url, { responseType: "blob" });
  const href = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(href);
};

// Mirrors package_upload_service.COLUMNS / EXAMPLE_ROWS (the template's own example).
const TEMPLATE_HEADERS = ["Pickup pincode*", "Delivery pincode*", "Dead Weight (kg)", "Length (cm)", "Breadth (cm)", "Height (cm)"];
const EXAMPLE_ROWS = [
  ["400062", "500061", "0.5", "10", "10", "10"],
  ["400063", "500062", "1.0", "20", "15", "10"],
];

const COLUMNS = [
  { key: "row", header: "Row", cell: (r) => `Row ${r.row}`, primary: true },
  { key: "from", header: "From pincode", cell: (r) => r.pickup_pincode },
  { key: "to", header: "To pincode", cell: (r) => r.drop_pincode },
  { key: "dead", header: "Dead wt (kg)", cell: (r) => r.dead_weight_kg, align: "right" },
  { key: "dims", header: "L × B × H (cm)", cell: (r) => `${r.length_cm} × ${r.breadth_cm} × ${r.height_cm}`, align: "right" },
  {
    key: "charged",
    header: "Charged (kg)",
    cell: (r) => <span className="font-semibold text-slate-800">{r.charged_weight_kg}</span>,
    align: "right",
  },
];

/**
 * Rate-calculator "Bulk upload" mode: the seller uploads a CSV/XLSX where every row is its own
 * lane (pickup + delivery pincode) and package. The backend validates every row (whole file
 * rejected with per-row errors) and returns the parsed rows; the parent rates them.
 *
 * props:
 *  - value: { count, rows } | null  — the parsed upload
 *  - onChange: (value | null) => void
 */
export function PackageUpload({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState(null); // { message, errors: [{row, error}] }
  const inputRef = useRef(null);

  const reset = () => {
    setFileName("");
    setError(null);
    onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onSelectFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(csv|xlsx)$/i.test(f.name)) {
      toast.error("Please choose a .csv or .xlsx file");
      e.target.value = "";
      return;
    }
    setFileName(f.name);
    setError(null);
    onChange(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/b2c/seller/orders/quote/packages`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && !Array.isArray(d)) {
        setError({ message: d.message || "Some rows have errors", errors: d.errors || [] });
      } else {
        setError({ message: typeof d === "string" ? d : "Could not read the file", errors: [] });
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="rc-upload">
      <div className="flex flex-col gap-3 rounded-lg bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <FileSpreadsheet className="h-8 w-8 shrink-0 text-brand" />
          <div>
            <p className="font-medium text-slate-800">One row per shipment lane</p>
            <p className="text-sm text-slate-500">
              Up to {MAX_ROWS} rows · pickup &amp; delivery pincode on every row · dead weight min 0.5 kg · L, B, H
              greater than 0
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            downloadBlob(
              `${API}/b2c/seller/orders/quote/packages/template`,
              "rate_calculator_bulk_template.xlsx"
            ).catch(() => toast.error("Failed to download template"))
          }
          data-testid="rc-upload-template"
          className="w-full sm:w-auto"
        >
          <Download className="mr-2 h-4 w-4" /> Download template
        </Button>
      </div>

      {/* Example of the expected format (same as the template's Instructions sheet). */}
      {!value && (
        <div className="space-y-1.5" data-testid="rc-upload-example">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Example</p>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  {TEMPLATE_HEADERS.map((h) => (
                    <th key={h} className="whitespace-nowrap px-3 py-2 text-left font-semibold text-slate-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {EXAMPLE_ROWS.map((r) => (
                  <tr key={r[0]}>
                    {r.map((v, i) => (
                      <td key={i} className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-600">{v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <label htmlFor="rc-upload-file" className="block text-sm font-medium text-slate-700">
          Select CSV or Excel file
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            id="rc-upload-file"
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx"
            onChange={onSelectFile}
            disabled={uploading}
            data-testid="rc-upload-file"
            className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:px-3 file:py-1 file:text-sm"
          />
          {(value || error) && (
            <Button type="button" variant="outline" onClick={reset} data-testid="rc-upload-clear" className="w-full sm:w-auto">
              <X className="mr-2 h-4 w-4" /> Clear
            </Button>
          )}
        </div>
        {uploading && (
          <p className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Reading {fileName}…
          </p>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3" role="alert" data-testid="rc-upload-error">
          <p className="flex items-center gap-2 text-sm font-medium text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error.message}
          </p>
          {error.errors.length > 0 && (
            <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto pl-6 text-sm text-red-700">
              {error.errors.map((e) => (
                <li key={e.row}>
                  <span className="font-medium">Row {e.row}:</span> {e.error}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {value && (
        <div className="space-y-3" data-testid="rc-upload-summary">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="flex items-center gap-1.5 text-slate-700">
              <Upload className="h-4 w-4 text-brand" />
              <span className="font-semibold">{value.count}</span> {value.count === 1 ? "row" : "rows"}
            </span>
          </div>
          <div className="max-h-80 overflow-y-auto">
            <DataTable columns={COLUMNS} data={value.rows} rowKey={(r) => r.row} density="compact" />
          </div>
        </div>
      )}
    </div>
  );
}

export default PackageUpload;
